/**
 * @format
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  FlatList,
  Image as RNImage,
  Platform,
  StyleSheet,
  TouchableHighlight,
  TouchableOpacity,
  View,
} from 'react-native';
import { Badge, Card, Icon, Image, Text } from 'react-native-elements';
import lodash from 'lodash';
import SwiperFlatList from 'react-native-swiper-flatlist';

import {
  Advertisement,
  FullCategory,
  Image as AdvertImage,
  Tag,
} from '../../api/adserver';
import config from '../../lib/config';
import ExploreImage from '../../components/ZTImage';

import { trackAdvertView } from '../../lib/analytics';
import { getTopImageUrl } from '../../lib/advert';

const debug = require('debug')('se:carousel');

const { width: windowWidth } = Dimensions.get('window');

const itemFeatureHeight = 240;
const widthFeaturePadding = 8;

const itemFeatureWidth =
  Platform.OS === 'web' ? 345 : windowWidth - (60 + widthFeaturePadding);
const totalFeatureItemWidth = itemFeatureWidth + widthFeaturePadding;

const itemWidthPadding = 20;
const itemHeight = 205;
const itemWidth = 219;
const itemCardPadding = 20;

const totalItemWidth = itemWidth + itemWidthPadding;

const widthMiniPadding = 12;
const itemMiniHeight = 55;
const itemMiniWidth = 70;
const totalMiniItemWidth = itemMiniWidth + widthMiniPadding;

const featureItemHeight =
  itemFeatureWidth * config.theme.advert.imageHeightRatio; // 194;

export interface SlideData {
  id: string;
  imageUrl: string;
  title?: string;
  subtitle?: string;
}

export interface AdvertSlideData {
  advert: Advertisement;
}

type onPressHandler = (item: any) => void;
type onPressCategoryHandler = (item: FullCategory) => void;

export interface SlideProps {
  data: SlideData;
  onPress?: onPressHandler;
  hideText?: boolean;
  margin?: number;
}

export interface PhotoSlideProps {
  data: AdvertImage;
  onPress?: onPressHandler;
  hideText?: boolean;
  margin?: number;
}

export interface AdvertSlideProps {
  data: AdvertSlideData;
  onPress?: onPressHandler;
  onPressCategory?: onPressCategoryHandler;
  hideText?: boolean;
  margin?: number;
  showViewMore?: boolean;
  onViewMore?: (item?: any) => void;
}

export interface MiniSlideData {
  id: string;
  icon: string;
  title?: string;
  local?: boolean;
}
export interface MiniSlideProps {
  data: MiniSlideData;
  onPress: onPressHandler;
  singleLine?: boolean;
}

export interface TagSlideProps {
  data: Tag;
  onPress: onPressHandler;
  isSelected: () => boolean;
}

export interface BadgeSlideProps {
  data: Tag;
  onPress: onPressHandler;
}

const Slide: React.FC<SlideProps> = ({ data, onPress }) => {
  return (
    <Card containerStyle={[styles.slideContainer]}>
      <TouchableHighlight
        onPress={() => {
          if (onPress) {
            onPress(data);
          }
        }}
      >
        <Card.Image source={{ uri: data.imageUrl }} resizeMode="cover" />
      </TouchableHighlight>
      <View style={styles.slideTextContainer}>
        {data.title && (
          <TouchableOpacity onPress={onPress}>
            <Text style={[styles.slideText, styles.slideTextLarge]}>
              {data.title}
            </Text>
          </TouchableOpacity>
        )}
        {data.subtitle && (
          <TouchableOpacity onPress={onPress}>
            <Text style={[styles.slideText, styles.slideTextMedium]}>
              {data.subtitle}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </Card>
  );
};

const AdvertSlide: React.FC<AdvertSlideProps> = ({
  data: { advert },
  onPress,
}) => {
  let imageUri = lodash.get(advert, 'hero_image.formats.medium.url', undefined);
  if (!imageUri) {
    imageUri = lodash.get(advert, 'hero_image.url', undefined);
  }
  return (
    <Card containerStyle={[styles.slideContainer]}>
      <TouchableOpacity
        onPress={() => {
          if (onPress) {
            onPress(advert);
          }
        }}
      >
        <ExploreImage uri={imageUri} style={styles.advertImage} />
      </TouchableOpacity>
      <View style={styles.slideTextContainer}>
        {advert.headline && (
          <TouchableOpacity onPress={onPress}>
            <Text style={[styles.slideText, styles.slideTextLarge]}>
              {advert.headline}
            </Text>
          </TouchableOpacity>
        )}
        {advert.bi_line && (
          <TouchableOpacity onPress={onPress}>
            <Text style={[styles.slideText, styles.slideTextMedium]}>
              {advert.bi_line}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </Card>
  );
};

const arrowSize = 26;
const arrowName = 'arrow-forward';

const ViewMoreFeatureSlide: React.FC<AdvertSlideProps> = ({
  data: { advert },
  onViewMore,
}) => {
  return (
    <TouchableOpacity
      onPress={() => {
        if (onViewMore) {
          onViewMore(advert);
        }
      }}
    >
      <View style={styles.featuredSlideContainer}>
        <View style={[styles.viewMoreSlide]}>
          <Icon
            tvParallaxProperties={null}
            name={arrowName}
            size={arrowSize}
            color={config.theme.advert.headingTextColor}
            style={styles.viewMoreArrow}
          />
          <Text>See More</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const ViewMoreAdvertSlide: React.FC<AdvertSlideProps> = ({
  data: { advert },
  onViewMore,
}) => {
  return (
    <Card containerStyle={[styles.slideContainer]}>
      <TouchableOpacity
        onPress={() => {
          if (onViewMore) {
            onViewMore(advert);
          }
        }}
      >
        <View style={styles.advertViewMoreSlideContainer}>
          <View style={[styles.viewMoreSlide]}>
            <Icon
              tvParallaxProperties={null}
              name={arrowName}
              size={arrowSize}
              color={config.theme.advert.headingTextColor}
              style={styles.viewMoreArrow}
              // iconStyle={styles.iconBase}
            />
            <Text>Find More</Text>
          </View>
        </View>
      </TouchableOpacity>
    </Card>
  );
};

const FeatureSlide: React.FC<AdvertSlideProps> = ({
  data: { advert },
  onPress,
}) => {
  const topImageUrl = getTopImageUrl(advert);
  const imageUrl = advert.hero_image ? advert.hero_image.url : topImageUrl;
  return (
    <TouchableOpacity
      onPress={() => {
        if (onPress) {
          onPress(advert);
        }
      }}
    >
      <View style={styles.featuredSlideContainer}>
        <ExploreImage uri={imageUrl} style={styles.featuredImage} />

        {!advert.hide_overlay_text && (
          <View style={styles.slideFeatureTextContainer}>
            {advert.headline && (
              <Text
                style={[styles.slideFeatureText, styles.slideFeatureTextLarge]}
              >
                {advert.headline}
              </Text>
            )}
            {advert.bi_line && (
              <Text
                style={[styles.slideFeatureText, styles.slideFeatureTextMedium]}
              >
                {advert.bi_line}
              </Text>
            )}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const PhotoSlide: React.FC<PhotoSlideProps> = ({ data }) => {
  return (
    <View style={styles.featuredSlideContainer}>
      <ExploreImage
        uri={data.url}
        resizeMode="contain"
        style={styles.photoImage}
      />
    </View>
  );
};

const MiniSlide: React.FC<MiniSlideProps> = ({
  data,
  onPress,
  singleLine = false,
}) => {
  const containerStyles: any[] = [styles.slideMiniContainer];
  const textStyles: any[] = [styles.slideMiniText];
  const textContainerStyles: any[] = [styles.slideMiniTextContainer];
  if (singleLine) {
    containerStyles.push(styles.slideMiniContainerSingleLine);
    textStyles.push(styles.slideMiniTextSingleLine);
    textContainerStyles.push(styles.slideMiniTextContainerSingleLine);
  }
  const imageParams: any = {
    style: styles.slideMiniImage,
    // defaultSource={{uri: data.icon}}
    resizeMode: 'contain',
    resizeMethod: 'scale',
    fadeDuration: 0,
    // resizeMode: 'cover',
  };
  if (data.local && Platform.OS === 'ios') {
    imageParams.defaultSource = { uri: data.icon };
  } else if (Platform.OS === 'android') {
    if (data.local) {
      imageParams.source = data.icon;
    } else {
      imageParams.source = { uri: data.icon };
    }
  } else {
    imageParams.source = { uri: data.icon };
  }
  return (
    <TouchableHighlight
      onPress={() => {
        if (onPress) {
          onPress(data);
        }
      }}
      activeOpacity={1.0}
      underlayColor="#cccccc"
    >
      <View style={containerStyles}>
        {data.icon && <RNImage {...imageParams} />}
        <View style={textContainerStyles}>
          {data.title && <Text style={textStyles}>{data.title}</Text>}
        </View>
      </View>
    </TouchableHighlight>
  );
};

const BadgeSlide: React.FC<BadgeSlideProps> = ({ data, onPress }) => {
  const style: any[] = [styles.slideBadgeContainer];
  const textStyle: any[] = [styles.slideTagsText];
  return (
    <>
      {data.name && (
        <TouchableHighlight
          onPress={() => {
            if (onPress) {
              onPress(data);
            }
          }}
          activeOpacity={1.0}
          style={style}
          underlayColor="#cccccc"
        >
          {data.name && <Text style={textStyle}>{data.name}</Text>}
        </TouchableHighlight>
      )}
    </>
  );
};

const TagSlide: React.FC<TagSlideProps> = ({ data, onPress, isSelected }) => {
  const style: any[] = [styles.slideTagsContainer];
  const textStyle: any[] = [styles.slideTagsText];
  if (isSelected()) {
    style.push(styles.slideTagsSelected);
    textStyle.push(styles.slideTagsSelectedText);
  }
  return (
    <TouchableHighlight
      onPress={() => {
        if (onPress) {
          onPress(data);
        }
      }}
      activeOpacity={1.0}
      style={style}
      underlayColor="#cccccc"
    >
      {data.name && <Text style={textStyle}>{data.name}</Text>}
    </TouchableHighlight>
  );
};

export enum DisplayType {
  adverts,
  badges,
  featured,
  full,
  mini,
  photo,
  tags,
}

export interface Props {
  placeholder?: string;
  displayType?: DisplayType;
  items?: Array<any>;
  selectedItems?: Array<any>;
  onPress?: (item: Advertisement) => void;
  onPressBadge?: (item: Tag) => void;
  onPressCategory?: (item: FullCategory) => void;
  onPressData?: (item: any) => void;
  onSelectedListChange?: (items: any[]) => void;
  onViewMore?: () => void;
  showViewMore?: boolean;
  name?: string;
  loop?: boolean;
  handleViewableItemsChanged?: (items: any) => void;
  singleLine?: boolean;
  initialSelected?: any[];
}

const CarouselFC: React.FC<Props> = (props) => {
  const {
    displayType,
    handleViewableItemsChanged,
    items,
    loop,
    name,
    onPress,
    onPressBadge,
    onPressCategory,
    onPressData,
    onSelectedListChange,
    onViewMore,
    showViewMore,
    singleLine,
    initialSelected,
  } = props;
  const [selectedItems, setSelectedItems] = useState<any>(
    initialSelected ? initialSelected : [],
  );
  useEffect(() => {
    setSelectedItems(initialSelected ? initialSelected : []);
  }, [initialSelected, setSelectedItems]);

  useEffect(() => {
    if (onSelectedListChange) {
      onSelectedListChange(selectedItems);
    }
  }, [onSelectedListChange, selectedItems]);

  const renderItem = useCallback(
    ({ item }: { item: any }) => {
      const onPressDataHandler = (itemData: any) => {
        debug(`on press data: ${itemData.id}`);
        if (onPressData) {
          onPressData(itemData);
        } else {
          debug('no onpressdata handler');
        }
      };

      const onPressHandler = (itemPress: Advertisement) => {
        debug(`on press: ${itemPress.id}`);
        if (onPress) {
          onPress(itemPress);
        } else {
          debug('no on press handler');
        }
      };

      const handleBadgeOnPress = (itemPress: Tag) => {
        debug(`on press: ${itemPress.id}`);
        if (onPressBadge) {
          onPressBadge(itemPress);
        } else {
          debug('no on press handler');
        }
      };

      const isSelected = (itemSelected: any) => {
        return !!lodash.find(
          selectedItems,
          (value) => value.id === itemSelected.id,
        );
      };

      const onToggleItem = (itemToggle: any) => {
        const found = isSelected(itemToggle);
        if (found) {
          // do nothing.  we want to leave selected
          // setSelectedItems([]);
        } else {
          setSelectedItems([itemToggle]);
        }
        // if (found) {
        //   setSelectedItems(
        //     lodash.filter(selectedItems, (value) => value.id !== itemToggle.id),
        //   );
        // } else {
        //   setSelectedItems([...selectedItems, itemToggle]);
        // }
      };

      if (item.showViewMore) {
        //  debug(`${name} - item.showViewMore is true`);
        switch (displayType) {
          case DisplayType.featured: {
            return (
              <ViewMoreFeatureSlide
                data={{ advert: item }}
                onPress={onPressHandler}
                onPressCategory={onPressCategory}
                margin={0}
                onViewMore={onViewMore}
                showViewMore={showViewMore}
              />
            );
          }
          case DisplayType.adverts: {
            return (
              <ViewMoreAdvertSlide
                data={{ advert: item }}
                onPress={onPressHandler}
                onPressCategory={onPressCategory}
                margin={0}
                onViewMore={onViewMore}
                showViewMore={showViewMore}
              />
            );
          }
        }
      }

      switch (displayType) {
        case DisplayType.mini: {
          return (
            <MiniSlide
              singleLine={singleLine}
              data={item}
              onPress={onPressDataHandler}
            />
          );
        }
        case DisplayType.badges: {
          return <BadgeSlide data={item} onPress={handleBadgeOnPress} />;
        }
        case DisplayType.tags: {
          return (
            <TagSlide
              data={item}
              onPress={onToggleItem}
              isSelected={() => isSelected(item)}
            />
          );
        }
        case DisplayType.featured: {
          return (
            <FeatureSlide data={{ advert: item }} onPress={onPressHandler} />
          );
        }
        case DisplayType.photo: {
          return (
            <PhotoSlide data={item} onPress={onPressDataHandler} margin={0} />
          );
        }
        case DisplayType.adverts: {
          return (
            <AdvertSlide
              data={{ advert: item }}
              onPress={onPressHandler}
              onPressCategory={onPressCategory}
              margin={0}
              onViewMore={onViewMore}
              showViewMore={showViewMore}
            />
          );
        }
        default: {
          return <Slide data={item} onPress={onPressDataHandler} />;
        }
      }
    },
    [
      displayType,
      onPress,
      onPressBadge,
      onPressCategory,
      onPressData,
      onViewMore,
      selectedItems,
      showViewMore,
      singleLine,
    ],
  );

  const keyGen = useCallback(
    (e: Advertisement, index: number) => {
      let key = `${name}-${e.id.toString()}-${index}`;
      return key;
    },
    [name],
  );

  let calcItemWidth: number;
  let decelerationRate: number | 'normal' | 'fast' | undefined = 'fast';
  let data: Array<any> = [];
  switch (displayType) {
    case DisplayType.mini:
    case DisplayType.tags:
    case DisplayType.badges:
      calcItemWidth = totalMiniItemWidth;
      decelerationRate = 'normal';
      if (items) {
        data = [...items];
      } else {
        data = []; // categoryList;
      }
      break;
    case DisplayType.featured:
      calcItemWidth = totalFeatureItemWidth;
      if (items) {
        data = [...items];
      } else {
        data = [];
      }
      break;
    case DisplayType.photo:
      calcItemWidth = totalFeatureItemWidth;
      if (items) {
        data = [...items];
      } else {
        data = []; // categoryList;
      }
      break;
    case DisplayType.adverts:
      if (items) {
        data = [...items];
      } else {
        data = []; // categoryList;
      }
      calcItemWidth = totalItemWidth + itemCardPadding;
      break;
    default:
      calcItemWidth = totalItemWidth + itemCardPadding;
  }

  if (showViewMore && data.length > 0) {
    // generate a somewhat unqiue key for the item
    let imageUri = '';
    if (lodash.isArray(items) && items.length > 0) {
      const item = items[0];
      imageUri = lodash.get(item, 'hero_image.formats.medium.url', undefined);
      if (!imageUri) {
        imageUri = lodash.get(
          item,
          'hero_image.url',
          displayType?.toString() || displayType,
        );
      }
    }
    // debug(`${name} - adding viewmore item to data array`);
    data.push({ showViewMore, id: `${name}-lastItem-${imageUri}` });
  }

  const windowSize =
    displayType === DisplayType.mini ||
    displayType === DisplayType.tags ||
    displayType === DisplayType.badges
      ? 1000
      : 5;

  const flatListOptimizationProps = {
    initialNumToRender: 0,
    maxToRenderPerBatch: 1,
    removeClippedSubviews: true,
    scrollEventThrottle: 16,
    windowSize,
    snapToInterval: calcItemWidth,
    keyExtractor: keyGen,
    getItemLayout: useCallback(
      (_, index) => ({
        length: calcItemWidth,
        offset: calcItemWidth * index,
        index,
      }),
      [calcItemWidth],
    ),
    showsHorizontalScrollIndicator: false,
    // props.displayType === DisplayType.mini ||
    // props.displayType === DisplayType.tags,
    bounces: true,
  };

  // TODO: check performance / re-renders here
  // debug(`R:render carousel:${props.displayType}`);
  // debug(`R:${name} - render more: ${showViewMore}`);

  const loopDelaySecs = 5;

  if (loop) {
    return (
      <SwiperFlatList
        data={data}
        style={styles.list}
        renderItem={renderItem}
        autoplay
        autoplayDelay={loopDelaySecs}
        autoplayLoop
        autoplayLoopKeepAnimation
        index={0}
        decelerationRate={decelerationRate}
        {...flatListOptimizationProps}
        viewabilityConfig={{
          minimumViewTime: config.advertTracking.carousel.minimumViewTime,
          itemVisiblePercentThreshold: undefined,
          viewAreaCoveragePercentThreshold:
            config.advertTracking.carousel.viewAreaCoveragePercentThreshold,
        }}
        onViewableItemsChanged={handleViewableItemsChanged}
      />
    );
  }

  return (
    <FlatList
      data={data}
      style={styles.list}
      renderItem={renderItem}
      decelerationRate={decelerationRate}
      pagingEnabled
      horizontal
      {...flatListOptimizationProps}
      viewabilityConfig={{
        minimumViewTime: config.advertTracking.carousel.minimumViewTime,
        viewAreaCoveragePercentThreshold:
          config.advertTracking.carousel.viewAreaCoveragePercentThreshold,
      }}
      onViewableItemsChanged={handleViewableItemsChanged}
    />
  );
};

export interface State {
  seenLast: boolean;
}

class Carousel extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { seenLast: false }; // to manage analytics reporting
    debug(
      `props for ${props.name} are: ${JSON.stringify(props.items?.length)}`,
    );
  }

  handleViewableItemsChanged = ({
    changed,
    viewableItems,
  }: {
    changed: any[];
    viewableItems: any[];
  }) => {
    if (this.state.seenLast || !this.props.items?.length) {
      // debug('has seen last.  not updating');
      return;
    }
    if (viewableItems.length > 0) {
      const lastItem = viewableItems[viewableItems.length - 1];
      // debug(
      //   `tracking advert view ${[
      //     lastItem.index,
      //     this.props.items?.length,
      //     lastItem.index === this.props.items?.length - 1,
      //   ]}`,
      // );

      if (lastItem.index === this.props.items?.length - 1) {
        debug(`setting setHasSeenLast for carousel: ${this.props.name}`);
        this.setState({
          seenLast: true,
        });
      }
    }
    // debug(`tracking advert view. Has seen last:${this.state.seenLast}`);
    changed
      .filter((view) => view.isViewable && view.item && view.item.campaign)
      .forEach((view) => {
        trackAdvertView(view.item);
      });
  };

  render() {
    return (
      <CarouselFC
        {...this.props}
        handleViewableItemsChanged={this.handleViewableItemsChanged}
      />
    );
  }
}

const advertImageWidth = 206;

const styles = StyleSheet.create({
  list: {
    flex: 1,
    width: '100%',
  },
  slideContainer: {
    // height: itemHeight,
    width: totalItemWidth,
    marginStart: 5,
    marginEnd: 16,
    marginBottom: 20,
    borderColor: 'transparent',
    shadowOffset: {
      width: 0,
      height: 3,
    },
    shadowOpacity: 0.6,
    shadowRadius: 3,
    elevation: 0, // android makes elevation calculations which get larger from orignal element :(
    ...Platform.select({
      ios: {},
      android: {
        borderWidth: 0.5,
        borderColor: '#efefef',
        borderBottomWidth: 2,
        borderRadius: 1,
      },
    }),
  },
  slideTagsSelected: {
    backgroundColor: config.theme.brandColor,
  },
  slideTagsSelectedText: {
    color: 'white',
  },
  slideFeatureContainer: {
    height: featureItemHeight,
    width: totalFeatureItemWidth,
    margin: 0,
  },
  slideTextContainer: {
    width: itemWidth,
    marginTop: 5,
  },
  slideFeatureTextContainer: {
    marginTop: 65 - itemFeatureHeight,
    width: itemFeatureWidth,
  },
  slideTagsTextContainer: {
    marginTop: 0,
    width: itemMiniWidth,
  },
  slideText: {
    color: config.theme.advert.headingTextColor,
  },
  slideTextLarge: {
    marginTop: 1,
    fontSize: 14,
    fontWeight: 'bold',
  },
  slideTextMedium: {
    fontSize: 11,
    fontWeight: '300',
    marginTop: 7,
  },
  slideFeatureText: {
    color: '#ffffff',
    backgroundColor: '#00000060',
    alignSelf: 'flex-start',
    padding: 7.5,
    paddingRight: 5,
    paddingLeft: 15,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  slideFeatureTextLarge: {
    fontSize: 20,
  },
  slideFeatureTextMedium: {
    fontSize: 14,
  },
  slideMiniText: {
    color: '#000000',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 0,
    paddingRight: 0,
    paddingLeft: 0,
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 0,
    marginTop: 5,
  },
  slideMiniContainer: {
    marginTop: 15,
    width: totalMiniItemWidth,
    alignItems: 'center',
    marginBottom: 20,
  },
  slideMiniContainerSingleLine: {
    width: undefined,
    marginHorizontal: 10,
  },
  slideMiniTextContainer: {
    marginTop: 0,
    width: itemMiniWidth,
  },
  slideMiniTextContainerSingleLine: {
    width: undefined,
    alignItems: 'center',
  },
  slideMiniTextSingleLine: {},
  slideTagsText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#707070',
  },
  slideTagsContainer: {
    width: 'auto',
    margin: 0,
    marginEnd: 10,
    marginTop: 10,
    padding: 7,
    paddingHorizontal: 10,
    borderRadius: 5,
    borderColor: '#e0e0e3',
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    backgroundColor: 'white',
  },
  slideBadgeContainer: {
    width: 'auto',
    margin: 0,
    marginEnd: 10,
    marginTop: 0,
    padding: 7,
    paddingHorizontal: 10,
    borderRadius: 5,
    borderColor: '#e0e0e3',
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    backgroundColor: 'white',
  },
  slideMiniImage: {
    width: itemMiniWidth - 22,
    height: itemMiniHeight - 22,
  },
  featuredImage: {
    width: itemFeatureWidth,
    // height: featureItemHeight,
    height: itemFeatureWidth * config.theme.advert.imageHeightRatio,
    borderRadius: 8,
  },
  featuredSlideContainer: {
    height: featureItemHeight,
    width: totalFeatureItemWidth,
    margin: 0,
  },
  advertImage: {
    borderWidth: config.theme.carousel.imageBorder,
    borderColor: config.theme.carousel.imageBorderColor,
    height: advertImageWidth * config.theme.advert.imageHeightRatio,
    width: advertImageWidth,
  },
  //These are the styles
  photoSlideContainer: {
    height: featureItemHeight,
    width: totalFeatureItemWidth,
    margin: 0,
  },
  photoImage: {
    width: itemFeatureWidth,
    height: featureItemHeight,
  },
  //End of the styles (maybe)
  badgeContainer: {
    marginRight: 10,
  },
  badgeBadge: {
    backgroundColor: config.theme.headingTextColor,
    height: 22,
    borderRadius: 30,
  },
  badgeText: {
    paddingHorizontal: 10,
  },
  viewMoreSlide: {
    flex: 1,
    justifyContent: 'center',
    textAlign: 'center',
    alignItems: 'center',
  },
  viewMoreArrow: {
    borderWidth: 1,
    borderColor: '#cccccc',
    borderRadius: 40,
    padding: 12,
    marginBottom: 16,
  },
  iconBase: {
    width: 24,
    height: 24,
  },
  advertViewMoreSlideContainer: {
    height: itemHeight - 34,
    width: totalItemWidth - 36,
    margin: 0,
    backgroundColor: 'white',
  },
});

export default Carousel;
